import { getTooltipAttributes } from "@common/contexts/Tooltip";
import Button from "@common/controls/Button/component";
import FullHeightContainer from "@common/controls/Container/FullHeight";
import ExternalLink from "@common/controls/Icon/ExternalLink/component";
import HelpIcon from "@common/controls/Icon/Help/component";
import Preloader from "@common/controls/Preloader/component";
import { type WithChildren, type WithRef } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import classNames from "classnames";
import { isFunction, isNull, isUndefined } from "lodash";
import React from "react";
import { FormContent } from "../component";

import "./style.less";

type FormBlockBaseProps = WithChildren &
  Partial<WithRef<HTMLDivElement>> & {
    actions?: React.JSX.Element | null;
    className?: string;
    contentStyle?: React.CSSProperties;
    footerActions?: React.JSX.Element;
    gap?: `${number}/${number}` | "default";
    isBorderless?: boolean;
    isFullHeight?: boolean;
    isLoading?: boolean | "overlay";
    isNarrow?: boolean;
    style?: React.CSSProperties;
    title?: any;
  };

const Container = (props: FormBlockBaseProps) => {
  const style = React.useMemo(
    () => ({
      ...props.style,
      "--gap": props.gap === "default" ? "3/4" : props.gap ?? "0"
    }),
    [props.style, props.gap]
  );

  const baseAttributes = {
    className: classNames("form-block boxed_container form_block", props.className, {
      narrow: props.isNarrow,
      borderless: props.isBorderless,
      loading: props.isLoading
    }),
    style
  };

  if (props.isFullHeight) {
    return (
      <FullHeightContainer isAutoHeight {...baseAttributes}>
        {props.children}
      </FullHeightContainer>
    );
  }

  return <div {...baseAttributes}>{props.children}</div>;
};

const FormBlockBase = (props: FormBlockBaseProps) => {
  const children = React.Children.toArray(props.children);
  const cwof = children.filter((x) => x["type"] !== FormBlockFooter);
  const footer = children.find((x) => x["type"] === FormBlockFooter);

  return (
    <Container {...props}>
      {((!isNull(props.title) && !isUndefined(props.title)) || !!props.actions) && (
        <div className="header">
          <span className="title">{props.title}</span>{" "}
          {props.actions && <span className="actions">{props.actions}</span>}
        </div>
      )}
      <FormContent ref={props.ref} style={props.contentStyle}>
        {props.isLoading === "overlay" ? (
          <>
            <Preloader /> {cwof}
          </>
        ) : props.isLoading ? (
          <Preloader />
        ) : (
          cwof
        )}
      </FormContent>

      {footer
        ? footer
        : !!props.footerActions && (
            <FormBlockFooter>
              <span className="actions">{props.footerActions}</span>
            </FormBlockFooter>
          )}
    </Container>
  );
};

export type FormBlockProps = Omit<FormBlockBaseProps, "hasGap">;
const FormBlock = (props: FormBlockBaseProps) => <FormBlockBase {...props} gap="default" />;
export default FormBlock;

type FormBlockEditProps = Omit<FormBlockBaseProps, "actions"> & {
  onEditClick?: () => void;
};
export const FormBlockEdit = (props: FormBlockEditProps) => {
  const i18n = useI18n();

  const actions = React.useMemo(
    () =>
      props.onEditClick ? (
        <Button onClick={props.onEditClick} type="link">
          {i18n.edit}
        </Button>
      ) : undefined,
    [props.onEditClick, i18n.edit]
  );
  return <FormBlockBase {...props} actions={actions} />;
};

export const FormBlockInfo = (props: FormBlockBaseProps) => <FormBlockBase {...props} />;

export const FormBlockFooter = (props: WithChildren) => <div className="footer">{props.children}</div>;

export type FormBlockInfoItemProps = {
  children: any;
  contentTooltip?: string;
  className?: string;
  orientation?: "horizontal" | "vertical";
  paddings?: "bottom" | "top";
  title?: string;
  titleTooltip?: string;
};
export const FormBlockInfoItem = (props: FormBlockInfoItemProps) => {
  const isEmpty = React.useMemo(
    () =>
      (!props.children && props.children !== 0) ||
      (Array.isArray(props.children) && props.children.every((x) => !x && x !== 0)),
    [props.children]
  );

  if (isEmpty) return null;

  return (
    <div className={classNames("info_item", props.className, `paddings_${props.paddings ?? "all"}`)}>
      {!!props.title && (
        <div className="title_container">
          <h5 className="title">{props.title}</h5>

          {props.titleTooltip && <HelpIcon tooltip={props.titleTooltip} />}
        </div>
      )}

      <div
        className={classNames("description", props.orientation ?? "horizontal")}
        {...getTooltipAttributes(props.contentTooltip)}
      >
        {props.children}
      </div>
    </div>
  );
};

type FormBlockInfoItemLeftRightProps = {
  bold?: "left" | "right" | "both";
  left: any;
  right: any;
  title: string;
};
export const FormBlockInfoItemLeftRight = (props: FormBlockInfoItemLeftRightProps) => (
  <FormBlockInfoItem title={props.title}>
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontWeight: props.bold === "left" || props.bold === "both" ? 600 : undefined }}>{props.left}</span>
      <span style={{ fontWeight: props.bold === "right" || props.bold === "both" ? 600 : undefined }}>
        {props.right}
      </span>
    </div>
  </FormBlockInfoItem>
);

type FormBlockExternalProps = {
  children: any;
  tooltip?: string;
  title: string;
  url?: string | (() => void);
};
export const FormBlockInfoItemExternal = (props: FormBlockExternalProps) => (
  <FormBlockInfoItem title={props.title}>
    <div className="frc fg sm">
      {props.children}
      {!!props.url && (
        <ExternalLink
          onClick={isFunction(props.url) ? props.url : () => window.open(props.url as string)}
          tooltip={props.tooltip}
        />
      )}
    </div>
  </FormBlockInfoItem>
);
